import { useRef, type ReactNode } from 'react';
import {
  Form,
  Formik,
  type FormikConfig,
  type FormikHelpers,
  type FormikValues,
} from 'formik';

import { Button } from '@design-system/button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@design-system/dialog';
import { cn } from '@design-system/helpers';
import { useDialogContext } from '@design-system/dialog/use-dialog-context';

type Intent = 'close' | 'another';

function ContentForm<Values extends FormikValues>({
  formKey,
  initialValues,
  validationSchema,
  editing,
  onSubmit,
  onClose,
  isPending,
  bodyClassName,
  children,
}: {
  formKey?: string;
  initialValues: Values;
  validationSchema: FormikConfig<Values>['validationSchema'];
  editing: boolean;
  onSubmit: (values: Values) => Promise<unknown>;
  onClose: () => void;
  isPending: boolean;
  bodyClassName?: string;
  children: ReactNode;
}) {
  const { formikRef } = useDialogContext();
  // Which footer button asked for the submit: the form is the same either way,
  // only what happens after the save differs.
  const intent = useRef<Intent>('close');

  const submit = async (values: Values, helpers: FormikHelpers<Values>) => {
    await onSubmit(values);
    if (intent.current === 'another') {
      helpers.resetForm();
      return;
    }
    // Reset first: a saved form is not "unsaved changes" the close guard
    // should hold the dialog open for.
    helpers.resetForm({ values });
    onClose();
  };

  return (
    <Formik<Values>
      key={formKey}
      innerRef={formikRef}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={submit}
    >
      {({ submitForm }) => (
        <Form className='flex min-h-0 flex-1 flex-col'>
          <DialogBody className={cn('flex flex-col gap-4 p-4', bodyClassName)}>
            {children}
          </DialogBody>
          <DialogFooter className='p-4'>
            {editing ? (
              <>
                <DialogClose asChild>
                  <Button
                    type='button'
                    variant='primaryOutline'
                    className='flex-1 border-pale-border text-gray-800'
                  >
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type='button'
                  variant='primary'
                  className='flex-1'
                  isLoading={isPending}
                  onClick={() => {
                    intent.current = 'close';
                    void submitForm();
                  }}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  type='button'
                  variant='primary'
                  className='flex-1'
                  isLoading={isPending}
                  onClick={() => {
                    intent.current = 'close';
                    void submitForm();
                  }}
                >
                  Confirm & Close
                </Button>
                <Button
                  type='button'
                  variant='primaryPale'
                  className='flex-1 border-primary-200'
                  disabled={isPending}
                  onClick={() => {
                    intent.current = 'another';
                    void submitForm();
                  }}
                >
                  Confirm & Add another
                </Button>
              </>
            )}
          </DialogFooter>
        </Form>
      )}
    </Formik>
  );
}

/**
 * The shell every "Add …" dialog shares: header with a title and subtitle,
 * the form body, and a footer that either saves and closes or saves and
 * clears the form for the next entry. In edit mode the footer is Cancel/Save.
 */
export function ContentFormDialog<Values extends FormikValues>({
  open,
  onOpenChange,
  title,
  subtitle,
  editing,
  formKey,
  initialValues,
  validationSchema,
  onSubmit,
  isPending,
  bodyClassName,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle: string;
  editing: boolean;
  formKey?: string;
  initialValues: Values;
  validationSchema: FormikConfig<Values>['validationSchema'];
  onSubmit: (values: Values) => Promise<unknown>;
  isPending: boolean;
  bodyClassName?: string;
  children: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        align='right'
        className='max-w-[600px] rounded-2xl'
      >
        <DialogHeader className='p-4'>
          <DialogTitle>{title}</DialogTitle>
          <p className='mt-1 text-sm font-normal text-gray-600'>{subtitle}</p>
        </DialogHeader>
        <ContentForm<Values>
          formKey={formKey}
          initialValues={initialValues}
          validationSchema={validationSchema}
          editing={editing}
          onSubmit={onSubmit}
          onClose={() => onOpenChange(false)}
          isPending={isPending}
          bodyClassName={bodyClassName}
        >
          {children}
        </ContentForm>
      </DialogContent>
    </Dialog>
  );
}
